import { useEffect } from 'react';
import { useChatState } from '../contexts/ChatStateContext';

/**
 * Hook to handle ChatStateContext pending actions
 * Integrates backend-driven chat state with existing UI systems
 * 
 * This replaces the old frontend onboarding flow handler in App.jsx
 * by listening to backend-determined actions instead of doing frontend logic
 */
export const useChatStateActions = (actions = {}) => {
  const { pendingActions, clearActions } = useChatState();
  const { 
    openMatrixPortal, 
    handleRunNewAnalysis, 
    getDateDaysAgo 
  } = actions;

  useEffect(() => {
    if (pendingActions.length > 0) {
      console.log('🎯 [useChatStateActions] Processing pending actions:', {
        actionCount: pendingActions.length,
        actions: pendingActions.map(a => ({
          type: a.type,
          timestamp: a.timestamp,
          hasData: !!a.data,
          onboardingContext: a.data?.onboardingContext
        }))
      });

      // Process each pending action
      pendingActions.forEach((action, index) => {
        console.log(`🎯 [useChatStateActions] Processing action ${index + 1}:`, {
          type: action.type,
          timestamp: action.timestamp,
          data: action.data
        });

        switch (action.type) {
          case 'show_property_modal':
            if (action.data?.onboardingContext) {
              console.log('🔴 [useChatStateActions] TRIGGER: Opening Matrix portal for onboarding property selection');
              if (openMatrixPortal) {
                openMatrixPortal();
              } else {
                console.error('🔴 [useChatStateActions] ERROR: openMatrixPortal function not provided');
              }
            } else {
              console.log('🔴 [useChatStateActions] TRIGGER: Would open property modal for non-onboarding context');
            }
            break;

          case 'start_onboarding_tour':
            console.log('🎯 [useChatStateActions] TRIGGER: Starting onboarding tour with auto-analysis');
            
            if (handleRunNewAnalysis && getDateDaysAgo) {
              // Get matched property from the action data
              const matchedProperty = action.data?.matched_property;
              const domainContext = action.data?.domain_context;
              
              if (matchedProperty) {
                console.log('🎯 [useChatStateActions] Auto-triggering analysis for:', matchedProperty);
                
                // Auto-trigger 30-day analysis (same logic as old system)
                const analysisFormData = {
                  dates: {
                    oldStartDate: getDateDaysAgo(60),
                    oldEndDate: getDateDaysAgo(31),
                    newStartDate: getDateDaysAgo(30),
                    newEndDate: getDateDaysAgo(1),
                    formattedDates: {
                      old_period: `${getDateDaysAgo(60)} - ${getDateDaysAgo(31)}`,
                      new_period: `${getDateDaysAgo(30)} - ${getDateDaysAgo(1)}`
                    }
                  },
                  country: '',
                  useCountryScaling: true,
                  collectHistorical: true,
                  selectedProperty: matchedProperty
                };
                
                handleRunNewAnalysis(analysisFormData);
                
                // Store context for ChatSidebar
                const onboardingContextData = {
                  type: 'auto_analysis_started',
                  domainContext: domainContext,
                  matchedProperty: matchedProperty,
                  message: `Perfect! I can see the property in your Search Console. I'm running a comprehensive analysis for the past 30 days to show you exactly what's happening with your SEO performance...`
                };
                
                console.log('🎯 [useChatStateActions] Setting onboardingContext:', onboardingContextData);
                sessionStorage.setItem('onboardingContext', JSON.stringify(onboardingContextData));
                
              } else {
                console.error('🎯 [useChatStateActions] ERROR: No matched property found in action data');
              }
            } else {
              console.error('🎯 [useChatStateActions] ERROR: Required functions not provided for onboarding tour');
            }
            break;

          case 'handle_error_state':
            console.log('❌ [useChatStateActions] TRIGGER: Would handle error state:', action.data?.error_state);
            // TODO: Implement error state handling
            break;

          default:
            console.log(`⚠️ [useChatStateActions] Unknown action type: ${action.type}`);
        }
      });

      // Clear all actions after processing
      console.log('🎯 [useChatStateActions] Clearing all processed actions');
      clearActions();
    }
  }, [pendingActions, clearActions, openMatrixPortal, handleRunNewAnalysis, getDateDaysAgo]);

  // Return some debugging info if needed
  return {
    hasPendingActions: pendingActions.length > 0,
    pendingActionCount: pendingActions.length,
    pendingActionTypes: pendingActions.map(a => a.type)
  };
};
